import { isNotNil } from '@newfront-insurance/core';
import type { BadgeProps } from '@newfront-insurance/core-ui';
import { IconCircleInformation, SingleObjectSelector, Tooltip } from '@newfront-insurance/core-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useProvider } from '@newfront-insurance/react-provision';
import { useRef, type JSX } from 'react';

import { AdminTRPCProvider } from '../../../../shared/providers/trpc';
import { AccountCard } from '../../components/account-card';
import { useAccountSwitcherOptions, useShouldShowProgramTypeByAccount, type Account } from '../../hooks';

// Re-export Account type for backwards compatibility
export type { Account };

interface Props {
  loggedInUserUuid: string;
  userScopes: string[];
  currentAccountUuid: string;
  onSelectAccount: (accountUuid: string, account?: Account) => void;
  hideTestAccountBadge?: boolean;
  selectorContainerClassName?: string;
  popoverPortalElement?: HTMLElement;
  customPopoverWidth?: string;
  customEmptyPlaceholder?: string;
  onClear?: () => void;
  inputContainerClassName?: string;
  inputClassName?: string;
  unfocusedInputContainerClassName?: string;
  unfocusedInputClassName?: string;
  renderSelectedItemOnHover?: boolean;
}

export function AccountSwitcher({
  loggedInUserUuid,
  userScopes,
  currentAccountUuid,
  onSelectAccount,
  hideTestAccountBadge = false,
  selectorContainerClassName,
  popoverPortalElement,
  customPopoverWidth,
  customEmptyPlaceholder,
  onClear,
  inputContainerClassName,
  inputClassName,
  unfocusedInputContainerClassName,
  unfocusedInputClassName,
  renderSelectedItemOnHover = true,
}: Props): JSX.Element | null {
  const { useQuery } = useProvider(AdminTRPCProvider);

  const { accounts, search, isLoading } = useAccountSwitcherOptions({
    loggedInUserUuid,
    userScopes,
  });

  const shouldShowProgramType = useShouldShowProgramTypeByAccount(accounts);

  const { data: selectedAccount, isLoading: isLoadingAccount } = useQuery([
    'accounts.getPublicByUuid',
    { accountUuid: currentAccountUuid },
  ]);
  const testAccountBadge: BadgeProps = { type: 'info', content: 'Test account' };

  const selectorContainerRef = useRef<HTMLDivElement>(null);

  const currentSelectorContainer = selectorContainerRef.current;

  const portalElement = popoverPortalElement ?? currentSelectorContainer;

  const popoverWidth =
    customPopoverWidth ?? (portalElement?.clientWidth ? `${portalElement.clientWidth}px` : undefined);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-0">
      <div className={cn('w-[250px]', selectorContainerClassName)} ref={selectorContainerRef}>
        <SingleObjectSelector<Account>
          emptyPlaceholder={customEmptyPlaceholder}
          popoverPortalElement={portalElement ?? undefined}
          popoverWidth={popoverWidth}
          isClearable={isNotNil(onClear)}
          onClear={onClear}
          itemToString={(account) => account?.name ?? ''}
          items={accounts.slice(0, 50)}
          isLoading={isLoading || isLoadingAccount}
          getItemKey={(account) => account.uuid}
          onSelect={(account) => {
            if (account) {
              onSelectAccount(account.uuid, account);
            }
          }}
          selectedItem={selectedAccount ?? null}
          onSearch={search}
          renderItem={(account, { isHovering, isSelected }) => (
            <AccountCard
              account={account}
              isExpandable={false}
              isHighlighted={isSelected}
              isHovering={isHovering}
              cardProps={{
                hideBorder: true,
              }}
              withProgramType={shouldShowProgramType(account.uuid)}
            />
          )}
          renderItemHover={(account) => (
            <div
              style={{
                width: 460,
              }}
            >
              <AccountCard
                account={account}
                isExpandable
                defaultExpanded
                cardProps={{
                  hideBorder: true,
                }}
                badgeProps={account?.isTestAccount ? testAccountBadge : undefined}
                withProgramType={shouldShowProgramType(account.uuid)}
              />
            </div>
          )}
          renderSelectedItemHover={
            renderSelectedItemOnHover
              ? (account) => (
                  <div
                    style={{
                      width: 460,
                    }}
                  >
                    <AccountCard
                      account={account}
                      isExpandable
                      defaultExpanded
                      cardProps={{
                        hideBorder: true,
                      }}
                      badgeProps={account?.isTestAccount ? testAccountBadge : undefined}
                      withProgramType={shouldShowProgramType(account.uuid)}
                    />
                  </div>
                )
              : undefined
          }
          renderSelectedItemHoverDelay={1500}
          inputClassName={inputClassName}
          inputContainerClassName={inputContainerClassName}
          unfocusedInputClassName={unfocusedInputClassName}
          unfocusedInputContainerClassName={unfocusedInputContainerClassName}
        />
      </div>
      {!hideTestAccountBadge && selectedAccount?.isTestAccount ? (
        <Tooltip message="This is a test account. Please only use it for testing purpose.">
          <div className="flex items-center gap-2 rounded-xs px-2 py-[2px]">
            <span className="text-glitter-500 block text-sm">Test Account</span>
            <IconCircleInformation />
          </div>
        </Tooltip>
      ) : undefined}
    </div>
  );
}
